import * as RadixDialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { forwardRef, type ComponentPropsWithoutRef, type ElementRef, type ReactNode } from 'react';

import { cn } from '../lib/cn';

/*
 * A drawer is a dialog that arrives from an edge.
 *
 * Built on the same Radix primitive as Dialog rather than hand-rolled, because
 * the requirements are identical and all of them are easy to get subtly wrong:
 * focus trap, Escape, scroll lock, `aria-modal`, and returning focus to the
 * trigger on close.
 *
 * It exists separately because the *shape* differs in a way that matters for
 * content: the media detail panel and the message reader are tall, scrolling,
 * and sit beside the list they came from. A centred modal for those means a
 * cramped box with its own scrollbar in the middle of the screen.
 */

export const Drawer = RadixDialog.Root;
export const DrawerTrigger = RadixDialog.Trigger;
export const DrawerClose = RadixDialog.Close;
export const DrawerTitle = RadixDialog.Title;
export const DrawerDescription = RadixDialog.Description;

export interface DrawerContentProps extends ComponentPropsWithoutRef<typeof RadixDialog.Content> {
  closeLabel: string;
  /**
   * Which edge it comes from. `inline-end` is the trailing edge — the right in
   * English, the left in Arabic — which is where a detail panel belongs in both.
   */
  side?: 'inline-end' | 'block-end';
}

export const DrawerContent = forwardRef<ElementRef<typeof RadixDialog.Content>, DrawerContentProps>(
  function DrawerContent({ className, children, closeLabel, side = 'inline-end', ...props }, ref) {
    return (
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="fixed inset-0 z-50 bg-overlay" />
        <RadixDialog.Content
          ref={ref}
          className={cn(
            'fixed z-50 flex flex-col bg-surface shadow-lg',
            side === 'inline-end'
              ? // `end-0` is logical: it resolves to right in LTR and left in
                // RTL, so one rule places the panel correctly in both. A
                // `right-0` here would put the Arabic drawer on the wrong side.
                // `inset-y-0` pins the vertical edges — Tailwind compiles it to
                // `inset-block: 0`, which is what full-height actually needs;
                // `inset-block-0` is not a real Tailwind class (silently
                // generates no CSS at all), which is what let this drawer render
                // with no height and drift wherever the document happened to be
                // tall, off the bottom of the viewport.
                'inset-y-0 end-0 w-full max-w-md border-s border-border'
              : 'start-0 end-0 bottom-0 max-h-[85dvh] rounded-t-lg border-t border-border',
            className,
          )}
          {...props}
        >
          <RadixDialog.Close
            className={cn(
              'absolute end-4 top-4 rounded-sm p-1 text-text-muted',
              'transition-colors hover:bg-surface-hover hover:text-text',
            )}
          >
            <X className="size-4" aria-hidden="true" />
            <span className="hs-sr-only">{closeLabel}</span>
          </RadixDialog.Close>
          {children}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    );
  },
);

export function DrawerHeader({ children }: { children: ReactNode }) {
  return (
    <div className="flex shrink-0 flex-col gap-1 border-b border-border p-6 pe-14">{children}</div>
  );
}

/** The scrolling region. Only the body scrolls, so the header stays put. */
export function DrawerBody({ children }: { children: ReactNode }) {
  return <div className="flex-1 overflow-y-auto p-6">{children}</div>;
}

export function DrawerFooter({ children }: { children: ReactNode }) {
  return (
    <div className="flex shrink-0 items-center justify-end gap-3 border-t border-border p-6">
      {children}
    </div>
  );
}
